import * as React from 'react';
import Input from 'core/components/Input';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import Pagination from 'core/components/Pagination';
import usePagination from 'core/hooks/usePagination';
import usePrevious from 'core/hooks/usePrevious';
import * as styles from './BlogEntries.module.scss';
import Categories from '../Categories';
import BlogCard from '../BlogCard';
import useSearch from './useSearch';
import { BlogCardData } from '../../lib/types';

export type BlogEntriesProps = {
    selectedTag?: string;
    selectedCategory?: string;
    entries: ReadonlyArray<BlogCardData>;
    page: number;
    perPage: number;
};

function createTo(
    toPage: number,
    selectedTag?: string,
    selectedCategory?: string,
    search?: string,
): string {
    const pathParts: string[] = ['blog'];

    const tagSlug = selectedTag ?? 'all';
    const categorySlug = selectedCategory ?? 'all';

    if (toPage > 1) {
        pathParts.push(categorySlug, tagSlug, toPage.toString(10));
    } else if (tagSlug !== 'all') {
        pathParts.push(categorySlug, tagSlug);
    } else if (categorySlug !== 'all') {
        pathParts.push(categorySlug);
    }

    return `/${pathParts.join('/')}${search}`;
}

function BlogEntries({
    selectedCategory,
    selectedTag,
    entries: allEntries,
    page,
    perPage,
}: BlogEntriesProps): React.ReactElement {
    const [searchTerm, setSearchTerm] = React.useState<string | null>(null);
    const result = useSearch(searchTerm || '', selectedCategory, selectedTag);
    const entriesToUse = searchTerm ? result : allEntries;
    const { entries, totalPages } = usePagination(page, perPage, entriesToUse);
    const location = useLocation();

    const toBuilder = React.useCallback(
        (toPage: number): string => {
            return createTo(
                toPage,
                selectedTag,
                selectedCategory,
                location?.search,
            );
        },
        [selectedTag, selectedCategory, location?.search],
    );

    const previousSearchTerm = usePrevious<string>(location?.search ?? '', '');

    // go to page 1 when the search term changes
    React.useEffect(() => {
        let timer: undefined | ReturnType<typeof setTimeout>;
        if (page > 1 && previousSearchTerm !== location?.search) {
            const to = createTo(
                1,
                selectedTag,
                selectedCategory,
                location?.search,
            );
            timer = setTimeout(() => {
                navigate(to, {
                    replace: true,
                });
                timer = undefined;
            }, 400);
        }

        return () => {
            if (timer !== undefined) {
                clearTimeout(timer);
            }
        };
    }, [
        previousSearchTerm,
        location?.search,
        selectedTag,
        selectedCategory,
        page,
    ]);

    // update query string when necessary
    React.useEffect(() => {
        const params = new URLSearchParams(location?.search);

        if (searchTerm === null) {
            // just booted
            const query = params.get('q') || '';
            setSearchTerm(query);
        } else if (searchTerm !== params.get('q')) {
            if (searchTerm) {
                params.set('q', searchTerm);
            } else {
                params.delete('q');
            }
        }

        const expectedSearch = params.toString();
        const currentSearch = (location?.search ?? '').substring(1);

        if (currentSearch !== expectedSearch) {
            navigate(`?${expectedSearch}`, { replace: true });
        }
    }, [location?.search, searchTerm]);

    const pagination = (
        <Pagination
            totalPages={totalPages}
            currentPage={page}
            toBuilder={toBuilder}
        />
    );
    return (
        <section className={styles.root}>
            <Input
                id="blogSearchString"
                name="blogSearchString"
                value={searchTerm || ''}
                onChange={({ target: { value } }) => setSearchTerm(value)}
                classes={{ root: styles.input }}
                placeholder="SEARCH..."
                rightIcon={<SearchIcon />}
            />
            <Categories
                selectedCategory={selectedCategory || 'all'}
                selectedTag={selectedTag || 'all'}
                searchTerm={searchTerm || ''}
            />

            {pagination}

            <div className={styles.entries}>
                {entries.map((entry) => (
                    <BlogCard entry={entry} key={entry.id} />
                ))}
            </div>

            {pagination}
        </section>
    );
}

export default BlogEntries;
